<template>
    <modal @close="emit('close')" id="modalComparisonChart">
        <template #header>
            Compare Symbols
        </template>
        <template #body>
            <div class="comparison-wrapper">
                <spinner v-if="dataLoading"></spinner>

                <div v-else v-for="(compare, index) in form.compares" :key="index">
                    <div class="flex items-center relative">
                        <div class="inline-block mr-4" :class="{ 'mt-6': !index }">
                            <span class="absolute top-0 font-bold text-xs leading-none mt-px pt-2" v-if="!index">Color</span>
                            <color-picker
                                :class="{'mt-6': !index}"
                                v-model="compare.color"
                                :initial-color="compare.color"
                                >
                            </color-picker>
                        </div>
                        <div class="inline-block w-full mr-2" v-if="Object.keys(cmdtyList).length !== 0">
                            <span class="absolute top-0 font-bold text-xs leading-none mt-px pt-2" v-if="!index">Active Symbol</span>
                            <select @change="selectIndex(index, $event.target.value)" v-model="compare.symbol" class="form-control form-control-sm align-bottom bg-white mb-3" :class="{'mt-8': !index}" aria-label="Active Symbol">
                                <option value="">Select Commodity</option>
                                <optgroup :label="cmdtyListKey" v-for="(cmdtyListItem, cmdtyListKey) in cmdtyList">
                                    {{ cmdtyListKey }}
                                    <template v-for="(v, k, i) in cmdtyListItem">
                                        <option v-for="ci in v" :value="ci.symbol">
                                            {{ci.symbolName}} - {{ci.contractMonthYear}}
                                        </option>
                                    </template>
                                </optgroup>
                            </select>
                        </div>
                        <div class="inline-block w-full mr-2" v-else>
                            <span class="absolute top-0 font-bold text-xs leading-none mt-px pt-2" v-if="!index">Active Symbol</span>
                            <input aria-label="Enter a symbol" type="text" :class="{'mt-8': !index}" class="form-control form-control-sm mb-2 mt-1" v-model="compare.symbol" placeholder="Enter a symbol....">
                        </div>
                        <div class="inline-block mr-4">
                            <span class="absolute top-0 font-bold text-xs leading-none -mt-px" v-if="!index">Left Scale</span>
                            <input class="form-control form-control-sm ml-2" :class="{'mt-6': !index, '-mt-1': index}" v-model="compare.leftScale" type="checkbox" name="">
                        </div>
                    </div>
                    <div class="w-full p-0 error" v-if="compare.error">
                        {{ compare.error }}
                    </div>
                    <div class="index-list" :name="compare" v-if="showList == index">
                        <ul class="list-none p-0" v-for="(itemList, keyIndexList) in indexList" :key="keyIndexList">
                            <li @click="selectIndex(index, itemList.value)">{{ itemList.name }}</li>
                        </ul>
                    </div>
                </div>
                <div class="form-group mt-2">
                    <label for="percentChangeScale" class="w-full sm:w-auto">
                        <input id="percentChangeScale" v-model="form.percentChangeScale" type="checkbox" name="" class="form-control form-control-sm mr-1">
                        Use %Chg Scale
                    </label>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-center">
                <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="emit('close')">Cancel</div>
                <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="compare">Apply</div>
            </div>
        </template>
    </modal>
</template>

<script setup>
import { ref, onMounted } from 'vue';

import ColorPicker from './ColorPicker';
import { useRequestInlineTemplate } from '../../mixins/RequestInlineTemplate';

const props = defineProps({
    initialComparison: {
        type: Object,
        default() {
            return {
                compares: []
            };
        }
    },
    section: String,
    scaleValue: String
});

const emit = defineEmits(['apply-comparison', 'close']);

const requestInlineTemplate = useRequestInlineTemplate();

const cmdtyList = ref({});
const indexList = ref([
    { value: '$SPX', name: 'S&P 500 Index' },
    { value: '$ONE', name: 'S&P 100 Index' },
    { value: '$DOWI', name: 'DOW Industrials' },
    { value: '$IUXX', name: 'Nasdaq 100 Index' },
    { value: '$NASX', name: 'Nasdaq Composite' },
    { value: '$SPX', name: 'NYSE Composite' },
    { value: '$NYA', name: 'AMEX Composite' },
    { value: '$XMI', name: 'Major Market Index' },
]);
const form = ref({
    compares: [
        props.initialComparison.compares[0] ||
        {
            color: '#2284DD',
            leftScale: false,
            error: false,
            symbol: ''
        },
        props.initialComparison.compares[1] ||
        {
            color: '#F18900',
            leftScale: false,
            error: false,
            symbol: ''
        },
        props.initialComparison.compares[2] ||
        {
            color: '#BC41CE',
            leftScale: false,
            error: false,
            symbol: ''
        }
    ],
    percentChangeScale: getInitialPercentChange()
});
const showList = ref(null);
const dataLoading = ref(false);

function getInitialPercentChange() {
    return props.scaleValue === 'percentChange';
}

const toggleList = (index) => {
    showList.value = (showList.value == index) ? null : index;
};

const selectIndex = (index, symbol) => {
    form.value.compares[index].symbol = symbol;
    form.value.compares[index].leftScale = true;
    form.value.compares[index].error = false;
    showList.value = null;
};

const reset = (index) => {
    form.value.compares[index].symbol = null;
    form.value.compares[index].leftScale = false;
    form.value.compares[index].error = false;
};

const compare = () => {
    const compares = form.value.compares.filter(c => c.symbol);

    const actions = [{
        id: 'Compare',
        context: {
            symbols: []
        }
    }];

    const symbols = [];

    compares.map((mCompare) => {
        actions[0].context.symbols.push({
            symbol: mCompare.symbol,
            curves: [{colors: [mCompare.color]}],
            leftScale: mCompare.leftScale
        });

        symbols.push(mCompare.symbol);
    });

    const promises = symbols.map(symbol => {
        return requestInlineTemplate.requestChartInlineTemplate(symbol);
    });

    if (form.value.percentChangeScale) {
        actions.push({
            id: 'Other',
            context: {
                comparisonMode: 'Percent'
            }
        });
    } else if (form.value.percentChangeScale === false && props.initialComparison.percentChangeScale) {
        actions.push({
            id: 'Other',
            context: {
                comparisonMode: 'None'
            }
        });
    }

    Promise.all(promises)
        .catch((error) => {
            if (error.response.status) {
                const request = JSON.parse(error.response.config.data);
                const compare = form.value.compares.find(c => c.symbol === request.symbol);
                compare.error = 'This symbol was not found';
                return Promise.reject();
            }
        }).then(() => {
            emit('apply-comparison', actions, form.value);
            emit('close');
        });
};

onMounted(() => {
    if (props.section === 'futures') {
        const route = window.route('api.future-contracts');
        dataLoading.value = true;

        axios.post(route, {})
            .then((response) => {
                cmdtyList.value = response.data;
            }).catch((error) => {
                console.error(error);
            }).then(() => {
                dataLoading.value = false;
            });
    }

});
</script>

<style lang="scss">
#modalComparisonChart {
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }

        .comparison-wrapper {
            @apply m-0 mt-2 mb-2 p-0 text-sm;

            .form-group-addon.form-group-addon-sm {
                margin-bottom: 0 !important;
                .input-addon-field,
                .input-addon-item {
                    box-shadow: none !important;
                }
            }

            .select-list-dropdown {
                @apply pr-6;
            }

            .show-list-icon {
                position: absolute;
                top: 8px;
                right: 7px;
                cursor: pointer;
            }

            .index-list {
                overflow-y: scroll;
                overflow-x: hidden;
                // margin-top: -5px;
                @apply max-h-48;
                @apply mt-1 mb-3 px-3 py-2 border border-gray-400 border-dotted;

                li {
                    @apply text-sm cursor-pointer my-1;
                    &:hover {
                        @apply text-blue-500;
                        i {
                            @apply text-blue-500;
                        }
                    }
                }
            }
        }
    }
}
</style>
